import { AppointmentRequestFlags } from "@physiofit-digital/physiofit_types";
import { useLocation } from "react-router-dom";
import { getAppointmentActionType } from "../../../core/store/appointmentSlice";
import {
  getCurrentPracticeAppointmentRequestFlags,
  getCurrentPracticeCustomAppointmentOptions,
} from "../../../core/store/chainSlice";
import { useMainStore } from "../../../core/store/mainStore";
import { useHandleParamNavigation } from "../../../core/utils/useHandleParamNavigation";
import { getTherapyDepartmentSkip } from "../../appointment/utils/appointmentFlow";
import { showsDetailOptions } from "../../appointment/utils/detailOptions";

export type AppointmentBookingStepNames =
  | "appointment" // step zero - before the start of the flow
  | "practice"
  | "department"
  | "type"
  | "recipeInfo"
  | "recipe"
  | "noRecipe"
  | "selfPayerDuration"
  | "selfPayerAdditionalTreatments"
  | "timeSlot"
  | "contact"
  | "success"
  | "phoneNumber"
  | "phoneVerification"
  | "cancellationAppointmentContact"
  | "cancellationAppointmentDateTime"
  | "modificationAppointmentDateTime"
  | "modificationAppointmentTimeSlots"
  | "modificationAppointmentContact"
  | "appointmentNotPossible"
  | "appointmentUnavailable"
  | "preferences";

export const useHandleAppointmentBookingNavigation = () => {
  const navigate = useHandleParamNavigation();
  const location = useLocation();
  const currentScreen = location.pathname.split("/").pop();
  const appointmentRequestFlags = useMainStore(
    getCurrentPracticeAppointmentRequestFlags,
  );
  const basePath = "/appointment";

  const navigateToCurrentStep = (forceScreen?: AppointmentBookingStepNames) => {
    const targetScreen =
      forceScreen ??
      getTargetScreen(
        currentScreen as AppointmentBookingStepNames,
        appointmentRequestFlags,
      );
    if (!targetScreen) return; // No further navigation possible
    const path = `${basePath}/${targetScreen}`;
    navigate(path);
  };

  return navigateToCurrentStep;
};

const getTargetScreen = (
  currentScreen: AppointmentBookingStepNames | undefined,
  appointmentRequestFlags?: AppointmentRequestFlags,
) => {
  const state = useMainStore.getState();
  const appointmentActionType = getAppointmentActionType(state);

  switch (currentScreen) {
    case "appointment":
      return "practice";
    case "practice":
      return getTherapyDepartmentSkip(state).isSkipped ? "type" : "department";
    case "department":
      return "type";
    case "type":
      if (
        appointmentActionType === "cancellation" ||
        appointmentActionType === "rescheduling"
      )
        return "phoneNumber";
      else return "recipeInfo";
    case "recipeInfo":
      return "recipe";
    case "recipe":
      return "timeSlot";
    case "noRecipe":
      return "selfPayerDuration";
    case "selfPayerDuration":
      return "selfPayerAdditionalTreatments";
    case "selfPayerAdditionalTreatments":
      return "timeSlot";
    case "timeSlot":
      if (
        appointmentActionType === "cancellation" ||
        appointmentActionType === "rescheduling"
      )
        return "contact";
      else if (
        showsDetailOptions(
          appointmentRequestFlags,
          getCurrentPracticeCustomAppointmentOptions(state),
        )
      )
        return "preferences";
      else return "contact";
    case "preferences":
      return "contact";
    case "contact":
      return "success";
    case "phoneNumber":
      if (appointmentActionType === "cancellation")
        return "cancellationAppointmentDateTime";
      else if (appointmentActionType === "rescheduling")
        return "modificationAppointmentDateTime";
      else return "timeSlot";
    case "cancellationAppointmentDateTime":
      return "cancellationAppointmentContact";
    case "cancellationAppointmentContact":
      return "success";
    case "modificationAppointmentDateTime":
      return "modificationAppointmentTimeSlots";
    case "modificationAppointmentTimeSlots":
      return "modificationAppointmentContact";
    case "modificationAppointmentContact":
      return "success";
    case "success":
      return undefined;
    default:
      return "practice";
  }
};
